// Drawer — a right-side (inline-end) slide-in panel for detail views that should
// keep the underlying list in context (vs. a centered modal that hides it).
// Glass-solid surface, scrim, Escape-to-close, focus trap on open, body-scroll
// lock, and direction-aware entry (slides from the end edge → right in LTR,
// left in RTL). Used by the Events surface and any "open detail without leaving
// the list" flow.
import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export function Drawer({
  open, onClose, title, subtitle, children, footer, width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Sticky footer (actions). */
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}) {
  const { t } = useTranslation('common');
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape to close + body-scroll lock + a focus trap (Tab/Shift+Tab wrap inside
  // the panel, as aria-modal asserts), restoring focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(panelRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      ) ?? []).filter((el) => el.offsetParent !== null || el === panelRef.current);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const els = focusables();
      if (els.length === 0) { e.preventDefault(); panelRef.current?.focus(); return; }
      const first = els[0], last = els[els.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the panel for keyboard users.
    const id = window.setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(id);
      // Return focus to wherever it was (the row/trigger) so keyboard users
      // don't lose their place in the underlying list.
      prevFocus?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  const widthClass = width === 'sm' ? 'max-w-sm' : width === 'lg' ? 'max-w-2xl' : 'max-w-md';
  const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

  return createPortal(
    <div className="fixed inset-0 z-drawer flex justify-end" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
      {/* Scrim */}
      <div className="absolute inset-0 animate-fade-in bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      {/* Panel — slides from the inline-end edge. */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative flex h-full w-full ${widthClass} flex-col border-s border-white/12 bg-gradient-to-b from-surface-2 to-surface shadow-2xl outline-none ${
          isRtl ? 'animate-drawer-in-left' : 'animate-drawer-in-right'
        }`}
      >
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-heading font-semibold">{title}</h2>
            {subtitle && <div className="mt-0.5 truncate text-label text-muted">{subtitle}</div>}
          </div>
          <button className="tk-icon-btn tk-btn-ghost -me-2 -mt-1.5 shrink-0" onClick={onClose} aria-label={t('actions.close')}>
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="border-t border-white/10 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
